<template>
  <div class="time-grouped-list">
    <template v-for="(group, index) in groupedItems" :key="index">
      <div class="group-container" :data-group-sort-order="group.sortOrder">
        <UiTimeGroupSeparator :label="group.label" class="group-separator" />
        <div class="group-items">
          <component 
            v-for="item in group.items" 
            :key="item.key" 
            :is="item.component" 
            v-bind="item.props" 
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'
import type { VNode } from 'vue'

interface Props {
  showDates?: boolean // Показывать точные даты вместо относительных меток
  reverse?: boolean   // Реверс порядка сортировки (старые сверху)
}

const props = withDefaults(defineProps<Props>(), {
  showDates: false,
  reverse: false
})

const { $dayjs } = useNuxtApp() as any
const slots = useSlots()

interface GroupedItem {
  component: any
  props: any
  key: string | number
  addDate: Date
}

interface TimeGroup {
  label: string
  items: GroupedItem[]
  sortOrder: number
}

// Функция для определения временной группы
const getTimeGroup = (date: Date): { label: string, sortOrder: number } => {
  const now = new Date()
  const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterdayStart = new Date(dayStart.getTime() - 24 * 60 * 60 * 1000)
  const weekStart = new Date(dayStart.getTime() - 7 * 24 * 60 * 60 * 1000)
  const monthStart = new Date(dayStart.getTime() - 30 * 24 * 60 * 60 * 1000)
  const threeMonthsStart = new Date(dayStart.getTime() - 90 * 24 * 60 * 60 * 1000)
  
  if (props.showDates) {
    // Режим показа дат - группируем по дням
    if (date >= dayStart) {
      return { label: 'Today', sortOrder: 9999999999 }
    } else if (date >= yesterdayStart) {
      return { label: 'Yesterday', sortOrder: 9999999998 }
    } else {
      // Для других дней показываем полную дату
      // Используем timestamp начала дня для уникального sortOrder
      const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      const sortOrder = Math.floor(dayStart.getTime() / 1000) // timestamp в секундах
      return { 
        label: $dayjs(date).format('MMMM D, YYYY'), 
        sortOrder 
      }
    }
  } else {
    // Стандартный режим - относительные группы
    if (date >= dayStart) {
      return { label: 'Today', sortOrder: 0 }
    } else if (date >= yesterdayStart) {
      return { label: 'Yesterday', sortOrder: 1 }
    } else if (date >= weekStart) {
      return { label: 'This week', sortOrder: 2 }
    } else if (date >= monthStart) {
      return { label: 'This month', sortOrder: 3 }
    } else if (date >= threeMonthsStart) {
      return { label: '3 months ago', sortOrder: 4 }
    } else {
      // Для старых сообщений используем timestamp для уникального sortOrder
      const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      const sortOrder = Math.floor(dayStart.getTime() / 1000) // timestamp в секундах
      return { label: 'Earlier', sortOrder }
    }
  }
}

// Извлекаем элементы из слота и группируем их
const groupedItems = computed<TimeGroup[]>(() => {
  const defaultSlot = slots.default?.()
  if (!defaultSlot) return []
  
  // Извлекаем все элементы с их add_date
  const items: GroupedItem[] = []
  
  const processVNodes = (vnodes: VNode[]) => {
    vnodes.forEach((vnode, index) => {
      if (vnode.type === 'template' || vnode.type === Symbol.for('v-fgt')) {
        // Обрабатываем фрагменты
        if (Array.isArray(vnode.children)) {
          processVNodes(vnode.children as VNode[])
        }
      } else if (vnode.props && typeof vnode.props === 'object') {
        // Ищем add_date в пропсах
        let addDate: string | null = null
        
        // Проверяем различные возможные места где может быть дата
        if (vnode.props.notification?.add_date) {
          addDate = vnode.props.notification.add_date
        } else if (vnode.props.group?.add_date) {
          addDate = vnode.props.group.add_date
        } else if (vnode.props.addDate) {
          addDate = vnode.props.addDate
        } else if (vnode.props['add-date']) {
          addDate = vnode.props['add-date']
        }
        
        if (addDate) {
          // Автоматическое определение формата даты
          let parsedDate: Date
          
          // Проверяем если это unix timestamp (строка из цифр длиной 10-13 символов)
          if (typeof addDate === 'string' && /^\d{10,13}$/.test(addDate)) {
            // Unix timestamp в секундах или миллисекундах
            const timestamp = parseInt(addDate)
            parsedDate = $dayjs.unix(timestamp).toDate()
          } else {
            // ISO строка или другой формат
            parsedDate = $dayjs(addDate).toDate()
          }
          
          items.push({
            component: vnode,
            props: vnode.props,
            key: (typeof vnode.key === 'string' || typeof vnode.key === 'number') ? vnode.key : `item-${index}`,
            addDate: parsedDate
          })
        }
      }
    })
  }
  
  processVNodes(defaultSlot)
  
  // Сортируем элементы по дате
  if (props.reverse) {
    // Реверс: старые сверху
    items.sort((a, b) => a.addDate.getTime() - b.addDate.getTime())
  } else {
    // Стандарт: новые сверху  
    items.sort((a, b) => b.addDate.getTime() - a.addDate.getTime())
  }
  
  // Группируем по временным периодам
  const groups = new Map<string, TimeGroup>()
  
  items.forEach(item => {
    const timeGroup = getTimeGroup(item.addDate)
    // Для режима showDates или старых сообщений используем только label как ключ,
    // чтобы избежать дублирования групп для одной даты
    const key = (props.showDates || timeGroup.label === 'Earlier') ? timeGroup.label : `${timeGroup.sortOrder}-${timeGroup.label}`
    
    if (!groups.has(key)) {
      groups.set(key, {
        label: timeGroup.label,
        items: [],
        sortOrder: timeGroup.sortOrder
      })
    }
    
    groups.get(key)!.items.push(item)
  })
  
  // Возвращаем группы отсортированные по sortOrder
  const sortedGroups = Array.from(groups.values()).sort((a, b) => b.sortOrder - a.sortOrder)
  
  // Применяем реверс к группам если нужно
  return props.reverse ? sortedGroups.reverse() : sortedGroups
})
</script>

<style scoped>
.time-grouped-list {
  display: flex;
  flex-direction: column;
}

.group-container {
  position: relative;
}

.group-separator {
  position: sticky;
  top: 0;
  z-index: 10; 
}

.group-items {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  /* Добавляем padding-top чтобы первый элемент не скрывался под sticky лейблом */
  padding-top: 8px;
}
</style>
